/**
 * Client interface for the data loader web worker
 */

import { type LayerConfig, type DataPoint } from '../types/metadata';

interface WorkerResponse {
  type: 'success' | 'error';
  requestId: string;
  data?: { data: DataPoint[][] };
  error?: string;
  cached?: boolean;
  mode?: 'points' | 'heatmap';
  resolution?: number;
  h3Cells?: string[]; // H3 resolution 2 cells used in the request
}

interface Viewport {
  latitude: number;
  longitude: number;
  zoom: number;
  bounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
}

// Logging utility
const log = {
  info: (message: string, data?: any) => {
    console.log(`[DataLoaderClient] ${message}`, data || '');
  },
  warn: (message: string, data?: any) => {
    console.warn(`[DataLoaderClient] ⚠️ ${message}`, data || '');
  },
  error: (message: string, data?: any) => {
    console.error(`[DataLoaderClient] ❌ ${message}`, data || '');
  },
  success: (message: string, data?: any) => {
    console.log(`[DataLoaderClient] ✅ ${message}`, data || '');
  },
};

class DataLoaderClient {
  private worker: Worker | null = null;
  private pendingRequests = new Map<string, {
    resolve: (value: { data: DataPoint[][], cached?: boolean, mode?: 'points' | 'heatmap', resolution?: number, h3Cells?: string[] }) => void;
    reject: (error: Error) => void;
  }>();
  private requestCounter = 0;

  constructor() {
    this.initWorker();
  }

  private initWorker() {
    try {
      log.info('Initializing data loader worker...');
      
      this.worker = new Worker(
        new URL('./dataLoader.worker.ts', import.meta.url),
        { type: 'module' }
      );

      this.worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
        const response = event.data;
        const pending = this.pendingRequests.get(response.requestId);

        if (!pending) {
          log.warn('Received response for unknown request', { requestId: response.requestId });
          return;
        }

        this.pendingRequests.delete(response.requestId);

        if (response.type === 'success' && response.data) {
          const pointCount = response.data.data[0]?.length || 0;
          log.success('Request completed', {
            requestId: response.requestId,
            cached: response.cached,
            mode: response.mode,
            points: pointCount,
            resolution: response.resolution,
            h3Cells: response.h3Cells?.length || 0,
          });
          pending.resolve({ 
            ...response.data, 
            cached: response.cached,
            mode: response.mode,
            resolution: response.resolution,
            h3Cells: response.h3Cells,
          });
        } else if (response.type === 'error') {
          log.error('Request failed', {
            requestId: response.requestId,
            error: response.error,
          });
          pending.reject(new Error(response.error || 'Unknown error'));
        }
      };

      this.worker.onerror = (error) => {
        log.error('Worker error', error);
        // Reject all pending requests
        this.pendingRequests.forEach(({ reject }, requestId) => {
          log.error('Rejecting pending request due to worker error', { requestId });
          reject(new Error('Worker error'));
        });
        this.pendingRequests.clear();
      };
      
      log.success('Worker initialized successfully');
    } catch (error) {
      log.error('Failed to initialize worker', error);
    }
  }

  /**
   * Load layer data with zoom-based loading
   * Automatically determines whether to load all points or use aggregations
   */
  async loadLayerData(
    config: LayerConfig,
    viewport: Viewport
  ): Promise<{ data: DataPoint[][], cached?: boolean, mode?: 'points' | 'heatmap', resolution?: number, h3Cells?: string[] }> {
    if (!this.worker) {
      throw new Error('Worker not initialized');
    }

    const requestId = `load-${++this.requestCounter}`;
    
    // Get a fresh auth token (refreshes if expired)
    const { useAuthStore } = await import('../stores');
    const idToken = await useAuthStore.getState().ensureValidToken();
    
    if (!idToken) {
      log.warn('No valid auth token available — token may have expired');
    }
    
    log.info('Loading layer data', {
      requestId,
      fileId: config.id,
      layerName: config.name,
      pointsCount: config.count,
      viewport: {
        lat: viewport.latitude.toFixed(4),
        lon: viewport.longitude.toFixed(4),
        zoom: viewport.zoom.toFixed(2),
      },
      hasToken: !!idToken,
    });

    return new Promise((resolve, reject) => {
      this.pendingRequests.set(requestId, { resolve, reject });

      this.worker!.postMessage({
        type: 'load',
        fileId: config.id,
        requestId,
        viewport,
        version: config.version,
        pointsCount: config.count, // Pass points count from LayerConfig
        schema: config.schema, // Pass schema to determine ID field
        heatmapThreshold: config.pointSettings?.heatmapThreshold, // Pass heatmap threshold from settings
        idToken, // Pass auth token to worker
      });

      // Timeout after 2 minutes
      setTimeout(() => {
        if (this.pendingRequests.has(requestId)) {
          this.pendingRequests.delete(requestId);
          log.error('Request timeout', { requestId, fileId: config.id });
          reject(new Error('Request timeout'));
        }
      }, 120000);
    });
  }

  /**
   * Clear cache for a specific file or all files
   */
  clearCache(fileId?: string) {
    if (!this.worker) return;

    log.info('Clearing cache', { fileId: fileId || 'all' });

    this.worker.postMessage({
      type: 'clearCache',
      fileId,
    });
  }

  /**
   * Terminate the worker
   */
  terminate() {
    if (this.worker) {
      log.info('Terminating worker');
      this.worker.terminate();
      this.worker = null;
      this.pendingRequests.clear();
      log.success('Worker terminated');
    }
  }
}

// Singleton instance
let dataLoaderInstance: DataLoaderClient | null = null;

export const getDataLoader = (): DataLoaderClient => {
  if (!dataLoaderInstance) {
    dataLoaderInstance = new DataLoaderClient();
  }
  return dataLoaderInstance;
};

export const terminateDataLoader = () => {
  if (dataLoaderInstance) {
    dataLoaderInstance.terminate();
    dataLoaderInstance = null;
  }
};
